import React, { useState, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import Markdown from "markdown-to-jsx";
import styles from "./TypewriterEffect.module.css";

interface TypewriterEffectProps {
    content: string;
    isStreaming: boolean;
    speed?: number; // milliseconds per character
    onComplete?: () => void;
}

export const TypewriterEffect: React.FC<TypewriterEffectProps> = ({
    content,
    isStreaming,
    speed = 10, // Even faster - 10ms per character
    onComplete
}) => {
    const [displayedContent, setDisplayedContent] = useState<string>("");
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isTyping, setIsTyping] = useState<boolean>(false);

    // Keep the original content with markdown formatting
    const cleanContent = useMemo(() => {
        if (!content) return "";
        return content; // Keep markdown formatting intact
    }, [content]);

    useEffect(() => {
        if (isStreaming) {
            // When streaming, show content immediately without typing effect
            setDisplayedContent(content);
            setIsTyping(false);
            return;
        }

        if (!cleanContent || cleanContent.length === 0) {
            setIsTyping(false);
            onComplete?.();
            return;
        }

        // Only start typing if we haven't already completed this content
        if (displayedContent === cleanContent) {
            setIsTyping(false);
            onComplete?.();
            return;
        }

        // Reset state when content changes
        setDisplayedContent("");
        setCurrentIndex(0);
        setIsTyping(true);

        // Start typing after a brief delay
        const initialDelay = setTimeout(() => {
            const typingInterval = setInterval(() => {
                setCurrentIndex(prevIndex => {
                    if (prevIndex >= cleanContent.length) {
                        clearInterval(typingInterval);
                        setIsTyping(false);
                        onComplete?.();
                        return prevIndex;
                    }

                    const nextIndex = prevIndex + 1;
                    const newText = cleanContent.slice(0, nextIndex);
                    setDisplayedContent(newText);

                    return nextIndex;
                });
            }, speed);

            return () => clearInterval(typingInterval);
        }, 100);

        return () => clearTimeout(initialDelay);
    }, [cleanContent, speed, isStreaming]); // Removed onComplete from dependencies to prevent restarts

    if (isStreaming) {
        // Show content directly when streaming with regular markdown
        const sanitizedHtml = DOMPurify.sanitize(content);
        return <Markdown>{sanitizedHtml}</Markdown>;
    }

    // Show progressive markdown formatting as we type
    const sanitizedContent = DOMPurify.sanitize(displayedContent);

    return (
        <div className={styles.typewriterContainer}>
            <div className={styles.typedContent}>
                <Markdown>{sanitizedContent}</Markdown>
            </div>
        </div>
    );
};
